import {
  Box,
  Checkbox,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { IChecklistSection } from 'features/inventory/checklist';
import React from 'react';

interface CompileSectionsProps {
  sections: IChecklistSection[];
  handleItemCheckChange?: (sectionIndex: number, itemIndex: number, checked: boolean) => void;
  loading: boolean;
  isTemplate?: boolean;
}

export const CompileSections = ({
  sections,
  handleItemCheckChange,
  loading,
  isTemplate = false
}: CompileSectionsProps) => {
  return (
    <>
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{
          borderRadius: 1,
          borderColor: '#e2e8f0',
          boxShadow: 'none',
          overflow: 'hidden'
        }}>
        <Table size="small" aria-label="checklist table">
          {/* Intestazione Tabella Fissa  */}
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8fafc' }}>
              <TableCell
                sx={{
                  py: 1.5,
                  px: 2,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  fontSize: '0.75rem',
                  letterSpacing: '0.05em',
                  color: '#0F334A',
                  borderBottom: '1px solid #e2e8f0'
                }}>
                Attività di controllo
              </TableCell>
              {!isTemplate && (
                <TableCell
                  sx={{
                    py: 1.5,
                    px: 2,
                    width: '180px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                    letterSpacing: '0.05em',
                    color: '#0F334A',
                    borderLeft: '1px solid #e2e8f0',
                    borderBottom: '1px solid #e2e8f0'
                  }}>
                  Data
                </TableCell>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {/* Iterazione Dinamica delle Sezioni */}
            {sections.map((section, sIdx) => {
              let periodChip = null;
              if (section.period === 'morning') {
                periodChip = (
                  <Chip
                    label="Mattina"
                    size="small"
                    variant="outlined"
                    sx={{
                      color: '#faad14',
                      borderColor: '#ffe58f',
                      backgroundColor: '#fffbe6',
                      fontWeight: 500,
                      height: '20px',
                      '& .MuiChip-label': { fontSize: '0.7rem', px: 1 }
                    }}
                  />
                );
              } else if (section.period === 'afternoon') {
                periodChip = (
                  <Chip
                    label="Pomeriggio"
                    size="small"
                    variant="outlined"
                    sx={{
                      color: '#1890ff',
                      borderColor: '#91d5ff',
                      backgroundColor: '#e6f7ff',
                      fontWeight: 500,
                      height: '20px',
                      '& .MuiChip-label': { fontSize: '0.7rem', px: 1 }
                    }}
                  />
                );
              }

              return (
                <React.Fragment key={section.id || sIdx}>
                  {/* Riga Titolo Sezione */}
                  <TableRow sx={{ bgcolor: '#f8fafcfa' }}>
                    <TableCell
                      colSpan={2}
                      sx={{
                        py: 1.25,
                        px: 2,
                        borderBottom: '1px solid #e2e8f0'
                      }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#0f172a' }}>
                          {section.title || `Sezione ${sIdx + 1}`}
                        </Typography>
                        {periodChip}
                      </Box>
                    </TableCell>
                  </TableRow>

                  {/* Lista dei Singoli Task Interni alla Sezione */}
                  {section.items?.map((item, iIdx) => {
                    const isLastItem = iIdx === section.items.length - 1;
                    return (
                      <TableRow
                        key={item.id || iIdx}
                        sx={{
                          '&:hover': { bgcolor: '#f8fafc66' }
                        }}>
                        {/* Colonna Sinistra: Checkbox e Descrizione del Task */}
                        <TableCell
                          sx={{
                            py: 1,
                            px: 2,
                            borderBottom: isLastItem ? '1px solid #e2e8f0' : '1px solid #f1f5f9'
                          }}>
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <Checkbox
                              checked={!!item.is_completed}
                              onChange={(e) => {
                                handleItemCheckChange?.(sIdx, iIdx, e.target.checked);
                              }}
                              disabled={loading || !handleItemCheckChange}
                              size="small"
                              sx={{
                                p: 0,
                                color: '#d9d9d9',
                                '&.Mui-checked': { color: '#1890ff' }
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{ color: '#334155', fontSize: '0.875rem', fontWeight: 500 }}>
                              {item.description}
                            </Typography>
                          </Box>
                        </TableCell>

                        {/* Colonna Destra: Timestamp */}
                        {!isTemplate && (
                          <TableCell
                            sx={{
                              py: 1,
                              px: 2,
                              borderLeft: '1px solid #f1f5f9',
                              borderBottom: isLastItem ? '1px solid #e2e8f0' : '1px solid #f1f5f9',
                              fontFamily: 'monospace',
                              fontSize: '0.85rem',
                              color: '#64748b'
                            }}>
                            {item.is_completed && item.updated_at ? (
                              new Date(item.updated_at)
                                .toLocaleString('it-IT', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })
                                .replace(',', ' -')
                            ) : item.is_completed ? (
                              new Date().toLocaleTimeString('it-IT', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })
                            ) : (
                              <Typography component="span" sx={{ color: '#cbd5e1' }}>
                                —
                              </Typography>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};
